// Share helpers per platform. Platform reality (2026):
//  - LinkedIn: the share-offsite URL takes ONLY a url, cannot prefill post text.
//  - X: intent/tweet prefills ONE tweet — no multi-tweet threads.
//  - Facebook: sharer takes only a url.
// Since a freshly-generated article has no public URL yet, "share the text" is
// impossible via URL params. So the honest UX for text-heavy platforms is:
// COPY the full text to the clipboard, THEN open the platform's composer, so the
// user pastes with one keystroke. Short-text platforms (X single tweet) can
// prefill directly. Every path copies first, so nothing is lost.

import type { ContentChannel } from '@traaker/shared';
import i18n from '../i18n';

export interface ShareResult {
  /** Toast to show after firing (what the user should do next). */
  toast: string;
}

export interface ShareIntent {
  label: string;
  /** Copies text (always) + opens the platform composer where one exists. */
  share: (text: string) => Promise<ShareResult>;
}

function openWindow(u: string) {
  window.open(u, '_blank', 'noopener,noreferrer');
}
async function copy(text: string) {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

/** X allows prefilling a SINGLE tweet. For a thread we prefill tweet 1 and copy
 *  the whole thing so the user can paste the rest. */
function firstTweet(text: string): string {
  const firstBlock = text.split(/\n\n+/)[0] || text;
  return firstBlock.slice(0, 270);
}

export function shareIntentFor(channel: ContentChannel): ShareIntent {
  switch (channel) {
    case 'x_post':
      return {
        label: i18n.t('shareIntents.x.label'),
        share: async (text) => {
          await copy(text);
          openWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text.slice(0, 270))}`);
          return { toast: i18n.t('shareIntents.x.toast') };
        },
      };
    case 'x_thread':
      return {
        // X has NO web way to post a multi-tweet thread — intent/tweet only ever
        // prefills ONE tweet. We open tweet 1 pre-filled; the UI renders the thread
        // tweet-by-tweet with per-tweet copy so the user chains the replies. A true
        // one-click thread needs the paid X API v2 + OAuth (deferred).
        label: i18n.t('shareIntents.x.label'),
        share: async (text) => {
          openWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(firstTweet(text))}`);
          return { toast: i18n.t('shareIntents.xThread.toast') };
        },
      };
    case 'linkedin_post':
    case 'linkedin_article':
      return {
        label: i18n.t('shareIntents.linkedin.label'),
        share: async (text) => {
          const ok = await copy(text);
          openWindow('https://www.linkedin.com/feed/?shareActive=true');
          return { toast: ok ? i18n.t('shareIntents.linkedin.toastCopied') : i18n.t('shareIntents.linkedin.toastManual') };
        },
      };
    case 'facebook':
      return {
        label: i18n.t('shareIntents.facebook.label'),
        share: async (text) => {
          await copy(text);
          openWindow('https://www.facebook.com/');
          return { toast: i18n.t('shareIntents.facebook.toast') };
        },
      };
    case 'threads':
      return {
        label: i18n.t('shareIntents.threads.label'),
        share: async (text) => {
          await copy(text);
          openWindow(`https://www.threads.net/intent/post?text=${encodeURIComponent(text.slice(0, 490))}`);
          return { toast: i18n.t('shareIntents.threads.toast') };
        },
      };
    case 'reddit':
      return {
        label: i18n.t('shareIntents.reddit.label'),
        share: async (text) => {
          await copy(text);
          const title = (text.split('\n')[0] || '').slice(0, 280);
          openWindow(`https://www.reddit.com/submit?title=${encodeURIComponent(title)}`);
          return { toast: i18n.t('shareIntents.reddit.toast') };
        },
      };
    default:
      // instagram, tiktok, youtube, quora, newsletter, blog_seo, landing_page, medium
      // → no useful web composer; copy is the action (handled by the Copier button).
      return {
        label: i18n.t('shareIntents.copy.label'),
        share: async (text) => {
          await copy(text);
          return { toast: i18n.t('shareIntents.copy.toast') };
        },
      };
  }
}

/** Channels that get a dedicated Share button (a real web composer exists). */
export function hasShareComposer(channel: ContentChannel): boolean {
  return ['x_post', 'x_thread', 'linkedin_post', 'linkedin_article', 'facebook', 'threads', 'reddit'].includes(channel);
}
